import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
    Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Paper, Tab, Tabs, TextField, Typography, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import EventIcon from '@mui/icons-material/Event';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useNavigate } from 'react-router-dom';
import AbsenceService from '../services/AbsenceService';
import EmployeeService from '../services/EmployeeService';
import AbsenceBalanceCard from './sub-components/absences/AbsenceBalanceCard';
import AbsenceTable from './sub-components/absences/AbsenceTable';
import AbsenceRequestDrawer from './sub-components/absences/AbsenceRequestDrawer';
import ConvertToSickDialog from './sub-components/absences/ConvertToSickDialog';
import Layout from './Layout';
import { AuthContext } from '../contexts/AuthContext';
import Roles from '../config/roles';

const ACCENT = '#427898';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function ProfileRow({ label, value }) {
    if (!value && value !== 0) return null;
    return (
        <Box sx={{ display: 'flex', gap: 2, py: 1.25, borderBottom: '1px solid #F1F5F9' }}>
            <Typography sx={{ fontSize: 13, color: '#94A3B8', minWidth: 160, flexShrink: 0 }}>{label}</Typography>
            <Typography sx={{ fontSize: 13, color: '#0F172A', fontWeight: 500 }}>{value}</Typography>
        </Box>
    );
}

function SectionCard({ icon, title, children }) {
    return (
        <Box sx={{ bgcolor: '#fff', border: '1px solid #EEF2F6', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #EEF2F6', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ color: ACCENT }}>{icon}</Box>
                <Typography fontWeight={700} fontSize={13} color="#0F172A" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</Typography>
            </Box>
            <Box sx={{ px: 3, py: 1 }}>
                {children}
            </Box>
        </Box>
    );
}

const CONTRACT_LABELS = {
    PERMANENT: 'Permanent', FIXED_TERM: 'Fixed-term',
    PART_TIME: 'Part-time', FREELANCE: 'Freelance', INTERNSHIP: 'Internship',
};

const SALARY_TYPE_LABELS = {
    MONTHLY_GROSS: 'Monthly (gross)', ANNUAL_GROSS: 'Annual (gross)',
    HOURLY: 'Hourly', MONTHLY_NET: 'Monthly (net)',
};

const DAY_LABELS = { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' };

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ─── My Profile tab ───────────────────────────────────────────────────────────

function MyProfileTab() {
    const { role } = useContext(AuthContext);
    const [employee, setEmployee] = useState(null);
    const [salaryHistory, setSalaryHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        Promise.all([
            EmployeeService.getMe(),
            EmployeeService.getMySalary().catch(() => []),
        ]).then(([emp, sal]) => {
            setEmployee(emp);
            setSalaryHistory(sal || []);
        }).catch(err => {
            setError(err?.response?.data?.message || 'Failed to load profile.');
        }).finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    }

    if (!employee) return null;

    const workingDaysList = (employee.workingDays || 'MON,TUE,WED,THU,FRI')
        .split(',').map(d => DAY_LABELS[d.trim()] || d.trim()).join(', ');

    const latestSalary = salaryHistory[0] ?? null;

    const roleLabel = role === Roles.ADMIN ? 'Administrator' : 'Employee';

    return (
        <Box sx={{ maxWidth: 680, mx: 'auto', pt: 3 }}>

            {/* Profile header */}
            <Box sx={{ bgcolor: '#fff', border: '1px solid #EEF2F6', borderRadius: 2, p: 3, mb: 2, display: 'flex', alignItems: 'center', gap: 2.5 }}>
                <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <PersonIcon sx={{ color: ACCENT, fontSize: 26 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={800} fontSize={19} color="#0F172A" lineHeight={1.2}>
                        {employee.firstName} {employee.lastName}
                    </Typography>
                    <Typography fontSize={13} color="#64748B" mt={0.4}>
                        {employee.position || '—'}
                        {employee.department ? ` · ${employee.department}` : ''}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    <Box sx={{ px: 1.25, py: 0.4, bgcolor: '#F0FDF4', borderRadius: '12px', border: '1px solid #BBF7D0' }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#16A34A' }}>{roleLabel}</Typography>
                    </Box>
                    {employee.accountStatus === 'ACTIVE' && (
                        <Box sx={{ px: 1.25, py: 0.4, bgcolor: '#EFF6FF', borderRadius: '12px', border: '1px solid #BFDBFE' }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#2563EB' }}>Active account</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Personal contact */}
            <SectionCard icon={<PersonIcon sx={{ fontSize: 16 }} />} title="Contact">
                <ProfileRow label="Email" value={employee.email} />
                <ProfileRow label="Phone" value={employee.phone} />
            </SectionCard>

            {/* Employment details */}
            <SectionCard icon={<WorkIcon sx={{ fontSize: 16 }} />} title="Employment">
                <ProfileRow label="Hire date" value={employee.hireDate ? formatDate(employee.hireDate) : null} />
                <ProfileRow label="Contract" value={employee.contractType ? (CONTRACT_LABELS[employee.contractType] || employee.contractType) : null} />
                <ProfileRow label="Status" value={employee.status} />
                <ProfileRow label="Employment" value={employee.employmentPercentage != null ? `${employee.employmentPercentage}%` : null} />
                <ProfileRow label="Weekly hours" value={employee.weeklyHours != null ? `${employee.weeklyHours}h / week` : null} />
                <ProfileRow label="Working days" value={workingDaysList} />
                <ProfileRow label="Schedule type" value={employee.workScheduleType === 'FULL_DAYS' ? 'Full days (fewer days)' : employee.workScheduleType === 'REDUCED_HOURS' ? 'Reduced hours (5 days)' : employee.workScheduleType} />
            </SectionCard>

            {/* Leave entitlement */}
            <SectionCard icon={<EventIcon sx={{ fontSize: 16 }} />} title="Leave entitlement">
                <ProfileRow label="Vacation days / year" value={employee.vacationDaysPerYear != null ? `${employee.vacationDaysPerYear} days` : null} />
            </SectionCard>

            {/* Salary */}
            {salaryHistory.length > 0 && (
                <SectionCard icon={<AccountBalanceWalletIcon sx={{ fontSize: 16 }} />} title="Salary">
                    {latestSalary && (
                        <>
                            <ProfileRow
                                label="Current salary"
                                value={`${Number(latestSalary.amount).toLocaleString('de-CH')} ${latestSalary.currency || 'CHF'} ${SALARY_TYPE_LABELS[latestSalary.type] ? `(${SALARY_TYPE_LABELS[latestSalary.type]})` : ''}`}
                            />
                            <ProfileRow label="Effective from" value={formatDate(latestSalary.effectiveDate)} />
                            {latestSalary.note && <ProfileRow label="Note" value={latestSalary.note} />}
                        </>
                    )}
                    {salaryHistory.length > 1 && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                                History
                            </Typography>
                            {salaryHistory.slice(1).map((s, i) => (
                                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: i < salaryHistory.length - 2 ? '1px solid #F1F5F9' : 'none' }}>
                                    <Typography sx={{ fontSize: 13, color: '#64748B' }}>
                                        {Number(s.amount).toLocaleString('de-CH')} {s.currency || 'CHF'}
                                        {SALARY_TYPE_LABELS[s.type] ? ` (${SALARY_TYPE_LABELS[s.type]})` : ''}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                                        from {formatDate(s.effectiveDate)}
                                    </Typography>
                                </Box>
                            ))}
                        </>
                    )}
                </SectionCard>
            )}
        </Box>
    );
}

// ─── Admin view ──────────────────────────────────────────────────────────────

function AdminAbsencesPage() {
    const [pending,       setPending]       = useState([]);
    const [rangeAbsences, setRangeAbsences] = useState([]);
    const [employees,     setEmployees]     = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [error,         setError]         = useState(null);

    const thisYear = new Date().getFullYear();
    const [rangeStart, setRangeStart] = useState(`${thisYear}-01-01`);
    const [rangeEnd,   setRangeEnd]   = useState(`${thisYear}-12-31`);
    const [filterEmployee, setFilterEmployee] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [saving,     setSaving]     = useState(false);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectNote,   setRejectNote]   = useState('');
    const [rejectSaving, setRejectSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [sickTarget,   setSickTarget]   = useState(null);

    const fetchPending = useCallback(async () => {
        const data = await AbsenceService.getPending();
        setPending(data);
    }, []);

    const fetchRange = useCallback(async () => {
        const data = await AbsenceService.getInRange(rangeStart, rangeEnd);
        setRangeAbsences(data);
    }, [rangeStart, rangeEnd]);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [, , emp] = await Promise.all([
                fetchPending(),
                fetchRange(),
                EmployeeService.getEmployees(0, 200),
            ]);
            setEmployees(emp.content ?? []);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load absences.');
        } finally {
            setLoading(false);
        }
    }, [fetchPending, fetchRange]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleApprove = async (absence) => {
        try {
            await AbsenceService.approve(absence.id);
            await Promise.all([fetchPending(), fetchRange()]);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to approve.');
        }
    };

    const openReject = (absence) => { setRejectTarget(absence); setRejectNote(''); };

    const confirmReject = async () => {
        setRejectSaving(true);
        try {
            await AbsenceService.reject(rejectTarget.id, rejectNote.trim() || null);
            setRejectTarget(null);
            await Promise.all([fetchPending(), fetchRange()]);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to reject.');
        } finally {
            setRejectSaving(false);
        }
    };

    const confirmDelete = async () => {
        try {
            await AbsenceService.delete(deleteTarget.id);
            setDeleteTarget(null);
            await Promise.all([fetchPending(), fetchRange()]);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to delete.');
        }
    };

    const confirmConvertToSick = async (splitStart, splitEnd, reason) => {
        try {
            await AbsenceService.convertToSick(sickTarget.id, splitStart, splitEnd, reason);
            setSickTarget(null);
            await fetchRange();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to convert.');
        }
    };

    const handleAddAbsence = async (data) => {
        const { employeeId, ...dto } = data;
        if (!employeeId) return;
        setSaving(true);
        try {
            await AbsenceService.create(employeeId, dto);
            setDrawerOpen(false);
            await Promise.all([fetchPending(), fetchRange()]);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to add absence.');
        } finally {
            setSaving(false);
        }
    };

    const filteredPending = filterEmployee
        ? pending.filter(a => `${a.employeeFirstName} ${a.employeeLastName}` === `${filterEmployee.firstName} ${filterEmployee.lastName}`)
        : pending;
    const filteredRange = filterEmployee
        ? rangeAbsences.filter(a => `${a.employeeFirstName} ${a.employeeLastName}` === `${filterEmployee.firstName} ${filterEmployee.lastName}`)
        : rangeAbsences;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1060, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography fontSize={22} fontWeight={800} color="#0F172A">Absences</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Autocomplete
                        options={employees}
                        getOptionLabel={e => `${e.firstName} ${e.lastName}`}
                        value={filterEmployee}
                        onChange={(_, v) => setFilterEmployee(v)}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        size="small" sx={{ width: 220 }}
                        renderInput={params => <TextField {...params} label="Filter by employee" />}
                    />
                    <Button
                        variant="contained" startIcon={<AddIcon />} onClick={() => setDrawerOpen(true)}
                        sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', whiteSpace: 'nowrap', '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}
                    >
                        Add Absence
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, mb: 3, overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #EEF2F6', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography fontWeight={700} fontSize={14} color="#0F172A">Pending Requests</Typography>
                    {filteredPending.length > 0 && (
                        <Box sx={{ bgcolor: '#FFFBEB', color: '#D97706', fontSize: 11, fontWeight: 700, borderRadius: 10, px: 1, py: 0.25, border: '1px solid #FDE68A' }}>
                            {filteredPending.length}
                        </Box>
                    )}
                </Box>
                <AbsenceTable absences={filteredPending} role={Roles.ADMIN} showEmployee onApprove={handleApprove} onReject={openReject} onDelete={setDeleteTarget} onConvertToSick={setSickTarget} />
            </Paper>

            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #EEF2F6', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography fontWeight={700} fontSize={14} color="#0F172A" sx={{ flex: '0 0 auto' }}>All Absences</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
                        <TextField type="date" size="small" label="From" value={rangeStart} onChange={e => setRangeStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 155 }} inputProps={{ style: { fontSize: 13 } }} />
                        <TextField type="date" size="small" label="To" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 155 }} inputProps={{ style: { fontSize: 13 } }} />
                        <Button variant="outlined" size="small" onClick={fetchRange} sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#CBD5E1', color: '#475569', fontSize: 13, whiteSpace: 'nowrap' }}>Apply</Button>
                    </Box>
                </Box>
                <AbsenceTable absences={filteredRange} role={Roles.ADMIN} showEmployee onApprove={handleApprove} onReject={openReject} onDelete={setDeleteTarget} onConvertToSick={setSickTarget} />
            </Paper>

            <AbsenceRequestDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onSave={handleAddAbsence} saving={saving} employees={employees} />

            <Dialog open={Boolean(rejectTarget)} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #EEF2F6', pb: 1.5 }}>Reject Request</DialogTitle>
                <DialogContent sx={{ pt: 2.5 }}>
                    <Typography fontSize={13} color="text.secondary" mb={2}>
                        Rejecting <strong>{rejectTarget?.employeeFirstName} {rejectTarget?.employeeLastName}</strong>'s <strong>{rejectTarget?.type?.replace(/_/g, ' ')}</strong> request{rejectTarget?.startDate ? ` for ${rejectTarget.startDate}` : ''}.
                    </Typography>
                    <TextField label="Rejection note (optional)" size="small" fullWidth multiline rows={3} value={rejectNote} onChange={e => setRejectNote(e.target.value)} placeholder="Reason for rejection…" />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: '1px solid #EEF2F6', gap: 1 }}>
                    <Button onClick={() => setRejectTarget(null)} sx={{ color: '#64748B', textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={confirmReject} disabled={rejectSaving} sx={{ bgcolor: '#EF4444', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}>
                        {rejectSaving ? 'Rejecting…' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #EEF2F6', pb: 1.5 }}>Delete Request</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Typography fontSize={13} color="text.secondary">
                        Delete <strong>{deleteTarget?.employeeFirstName} {deleteTarget?.employeeLastName}</strong>'s <strong>{deleteTarget?.type?.replace(/_/g, ' ')}</strong> request? This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: '1px solid #EEF2F6', gap: 1 }}>
                    <Button onClick={() => setDeleteTarget(null)} sx={{ color: '#64748B', textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={confirmDelete} sx={{ bgcolor: '#EF4444', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}>Delete</Button>
                </DialogActions>
            </Dialog>

            <ConvertToSickDialog open={Boolean(sickTarget)} absence={sickTarget} onClose={() => setSickTarget(null)} onConfirm={confirmConvertToSick} />
        </Box>
    );
}

// ─── Employee self-service view ───────────────────────────────────────────────

function EmployeeAbsencesPage() {
    const { role } = useContext(AuthContext);
    const navigate = useNavigate();

    const [tab,               setTab]               = useState(0); // 0=Absences 1=My Profile
    const [absences,          setAbsences]          = useState([]);
    const [summary,           setSummary]           = useState(null);
    const [loading,           setLoading]           = useState(true);
    const [notLinked,         setNotLinked]         = useState(false);
    const [fetchError,        setFetchError]        = useState(null);
    const [drawerOpen,        setDrawerOpen]        = useState(false);
    const [saving,            setSaving]            = useState(false);
    const [submitError,       setSubmitError]       = useState(null);
    const [overlappingNames,  setOverlappingNames]  = useState([]);
    const [cancelTarget,      setCancelTarget]      = useState(null);

    const fetchAll = useCallback(async () => {
        const [abs, sum] = await Promise.all([
            AbsenceService.getMy(),
            AbsenceService.getMySummary(),
        ]);
        setAbsences(abs);
        setSummary(sum);
    }, []);

    useEffect(() => {
        setLoading(true);
        setNotLinked(false);
        setFetchError(null);
        fetchAll()
            .catch(err => {
                const status = err?.response?.status;
                if (status === 404) setNotLinked(true);
                else setFetchError(err?.response?.data?.message || 'Failed to load absence data.');
            })
            .finally(() => setLoading(false));
    }, [fetchAll]);

    const handleSave = async (data) => {
        setSaving(true);
        setSubmitError(null);
        try {
            await AbsenceService.createMy(data);
            // Close drawer immediately on success, then refresh in background
            setDrawerOpen(false);
            setOverlappingNames([]);
            fetchAll().catch(() => {});
        } catch (err) {
            // Show error inside the drawer instead of behind it
            setSubmitError(err?.response?.data?.message || 'Failed to submit request. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (absence) => {
        if (absence.status !== 'PENDING') return;
        setCancelTarget(absence);
    };

    const confirmCancel = async () => {
        await AbsenceService.delete(cancelTarget.id);
        setCancelTarget(null);
        await fetchAll();
    };

    const handleOverlapCheck = async (empId, start, end) => {
        try {
            const names = await AbsenceService.overlapCheck(empId, start, end);
            setOverlappingNames(names);
        } catch {
            setOverlappingNames([]);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    if (notLinked) {
        const isAdmin = role === Roles.ADMIN;
        return (
            <Box sx={{ maxWidth: 520, mx: 'auto', mt: 8, px: 3 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <BeachAccessIcon sx={{ fontSize: 56, color: '#CBD5E1', mb: 2 }} />
                    <Typography variant="h6" fontWeight={700} color="#0F172A" gutterBottom>No employee profile linked</Typography>
                    <Typography color="text.secondary" fontSize={14}>Your user account isn't linked to an employee record yet.</Typography>
                </Box>
                {isAdmin ? (
                    <Box sx={{ p: 2.5, bgcolor: '#F0F9FF', borderRadius: 2, border: '1px solid #BFDBFE' }}>
                        <Typography fontSize={13} fontWeight={700} color="#1E40AF" mb={1}>How to link your account:</Typography>
                        <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            <Typography component="li" fontSize={13} color="#1E3A5F">Go to <strong>Employees</strong> and create an employee record for yourself.</Typography>
                            <Typography component="li" fontSize={13} color="#1E3A5F">On your employee profile, click <strong>"Provision Account"</strong> and enter your current login email.</Typography>
                            <Typography component="li" fontSize={13} color="#1E3A5F">Your existing account will be linked automatically — no new account will be created.</Typography>
                        </Box>
                        <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/employees')}
                            sx={{ mt: 2, bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}>
                            Go to Employees
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ p: 2.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0', textAlign: 'center' }}>
                        <Typography fontSize={13} color="#64748B">Please contact your administrator to set up your employee profile.</Typography>
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Typography fontSize={22} fontWeight={800} color="#0F172A">My Workspace</Typography>
                {tab === 0 && (
                    <Button
                        variant="contained" startIcon={<AddIcon />} onClick={() => { setSubmitError(null); setDrawerOpen(true); }}
                        sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}
                    >
                        Request absence
                    </Button>
                )}
            </Box>

            {/* Tabs */}
            <Tabs
                value={tab} onChange={(_, v) => setTab(v)}
                sx={{ mb: 3, borderBottom: '1px solid #EEF2F6', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 14, color: '#64748B' }, '& .Mui-selected': { color: ACCENT }, '& .MuiTabs-indicator': { bgcolor: ACCENT } }}
            >
                <Tab label="Absences" />
                <Tab label="My Profile" />
            </Tabs>

            {/* Absences tab */}
            {tab === 0 && (
                <>
                    {fetchError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFetchError(null)}>{fetchError}</Alert>}

                    {summary && (
                        <Box sx={{ mb: 3 }}>
                            <AbsenceBalanceCard summary={summary} />
                        </Box>
                    )}

                    <AbsenceTable absences={absences} role={Roles.USER} onDelete={handleDelete} />
                </>
            )}

            {/* My Profile tab */}
            {tab === 1 && <MyProfileTab />}

            {/* Absence request drawer */}
            <AbsenceRequestDrawer
                open={drawerOpen}
                onClose={() => { setDrawerOpen(false); setOverlappingNames([]); setSubmitError(null); }}
                onSave={handleSave}
                saving={saving}
                submitError={submitError}
                overlappingNames={overlappingNames}
                onOverlapCheck={handleOverlapCheck}
            />

            {/* Cancel dialog */}
            <Dialog open={Boolean(cancelTarget)} onClose={() => setCancelTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #EEF2F6', pb: 1.5 }}>Cancel Request</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Typography fontSize={13} color="text.secondary">
                        Are you sure you want to cancel your <strong>{cancelTarget?.type?.replace(/_/g, ' ')}</strong> request{cancelTarget?.startDate ? ` for ${cancelTarget.startDate}` : ''}?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: '1px solid #EEF2F6', gap: 1 }}>
                    <Button onClick={() => setCancelTarget(null)} sx={{ color: '#64748B', textTransform: 'none' }}>Keep it</Button>
                    <Button variant="contained" onClick={confirmCancel} sx={{ bgcolor: '#EF4444', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}>Cancel request</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ─── Root export ──────────────────────────────────────────────────────────────

export default function MyAbsencesPage() {
    const { role } = useContext(AuthContext);

    return (
        <Layout>
            {role === Roles.ADMIN
                ? <AdminAbsencesPage />
                : <EmployeeAbsencesPage />
            }
        </Layout>
    );
}
